'use client';

import { IDataPoolEntry, Citation } from '@/types';
import { useState, useCallback, useMemo, useEffect } from 'react';
import { parseStepPath, getValueAtPath, formatValue } from '@/_utils/citationPath';
import JsonViewer from '@/_components/JsonViewer/JsonViewer';

interface ProvenanceTabProps {
	dataPoolEntries?: IDataPoolEntry[];
	citations: Citation[];
	highlightedPath?: string | null;
	scrollRequest?: { index: number; timestamp: number } | null;
	onCitationClick?: (jsonPath: string) => void;
}

// Step Accordion Component
function StepAccordion({
	entry,
	stepIndex,
	isExpanded,
	onToggle,
	citations,
	highlightedPath,
	onCitationClick,
	onSwitchToRaw,
	viewMode,
	allEntries
}: {
	entry: IDataPoolEntry;
	stepIndex: number;
	isExpanded: boolean;
	onToggle: () => void;
	citations: Citation[];
	highlightedPath: string | null;
	onCitationClick: (path: string) => void;
	onSwitchToRaw: () => void;
	viewMode: 'citations' | 'raw';
	allEntries: IDataPoolEntry[];
}) {
	const stepPrefix = `step_${stepIndex + 1}`;
	const stepCitations = citations.filter((c) => c.jsonPath.startsWith(stepPrefix));
	const isFailed = entry.failed === true;

	// Create highlighted and expanded paths for this step
	const highlightedPaths = useMemo(() => {
		const paths = new Set<string>();
		stepCitations.forEach((c) => paths.add(c.jsonPath));
		if (highlightedPath) paths.add(highlightedPath);
		return paths;
	}, [stepCitations, highlightedPath]);

	const expandedPaths = useMemo(() => {
		const paths = new Set<string>();
		if (highlightedPath) {
			const { stepIndex: hStepIdx, innerPath } = parseStepPath(highlightedPath);
			if (hStepIdx === stepIndex) {
				// Add parent paths
				const parts = innerPath
					.replace(/\[(\d+)\]/g, '.$1')
					.split('.')
					.filter(Boolean);
				let current = stepPrefix;
				paths.add(current);
				for (let i = 0; i < parts.length; i += 1) {
					const part = parts[i];
					current = /^\d+$/.test(part) ? `${current}[${part}]` : `${current}.${part}`;
					paths.add(current);
				}
			}
		}
		return paths;
	}, [highlightedPath, stepIndex, stepPrefix]);

	return (
		<div
			className={`rounded-lg border ${isFailed ? 'border-red-500/50 bg-red-900/10' : 'border-border bg-secondary/30'}`}
		>
			{/* Accordion Header */}
			<button
				type='button'
				onClick={onToggle}
				className='hover:bg-accent/30 flex w-full items-center gap-3 p-3 text-left'
			>
				<span className='text-muted-foreground'>{isExpanded ? '▼' : '▶'}</span>
				<span
					className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded text-xs font-medium ${
						isFailed
							? 'bg-red-500/20 text-red-600 dark:text-red-400'
							: 'bg-violet-500/20 text-violet-600 dark:text-violet-400'
					}`}
				>
					{isFailed ? '✕' : stepIndex + 1}
				</span>
				<div className='min-w-0 flex-1'>
					<span className={`text-sm ${isFailed ? 'text-red-600 dark:text-red-300' : 'text-foreground'}`}>
						Step {stepIndex + 1}
						{isFailed && <span className='ml-2 text-red-400'>(Failed)</span>}
					</span>
					{!isFailed && stepCitations.length > 0 && (
						<span className='ml-2 rounded bg-cyan-500/20 px-1.5 py-0.5 text-xs text-cyan-600 dark:text-cyan-400'>
							{stepCitations.length}
						</span>
					)}
				</div>
				<div className='text-muted-foreground flex items-center gap-2 text-xs'>
					<span className='bg-secondary rounded px-1.5 py-0.5 uppercase'>{entry.dataSource}</span>
				</div>
			</button>

			{/* Accordion Content */}
			{isExpanded && (
				<div className='border-border border-t p-3'>
					{/* Step Metadata or Failure Reason */}
					{isFailed ? (
						<div className='mb-3 rounded bg-red-500/10 p-2 text-xs text-red-600 dark:text-red-300'>
							<span className='font-medium'>Failure Reason: </span>
							{entry.failureReason || 'Unknown error'}
						</div>
					) : (
						<div className='text-muted-foreground mb-3 flex flex-wrap items-center gap-2 text-xs'>
							{entry.endpoint && (
								<>
									<span className='text-muted-foreground'>→</span>
									<code className='text-muted-foreground max-w-[200px] truncate'>{entry.endpoint}</code>
								</>
							)}
							<span className='text-muted-foreground'>•</span>
							<span>{new Date(entry.fetchedAt).toLocaleTimeString()}</span>
						</div>
					)}

					{/* Only show data viewer for successful steps */}
					{!isFailed &&
						(viewMode === 'citations' ? (
							<div className='space-y-2'>
								{stepCitations.length === 0 ? (
									<p className='text-muted-foreground text-xs'>No citations for this step</p>
								) : (
									stepCitations.map((citation) => {
										const value = getValueAtPath(allEntries, citation.jsonPath);
										const isHighlighted = highlightedPath === citation.jsonPath;
										return (
											<button
												key={`${citation.index}-${citation.jsonPath}`}
												type='button'
												id={`citation-item-${citation.index}`}
												onClick={() => {
													onCitationClick(citation.jsonPath);
													onSwitchToRaw();
													// Scroll to raw data element after view switch
													setTimeout(() => {
														const element = document.getElementById(`json-path-${citation.jsonPath}`);
														if (element) {
															element.scrollIntoView({ behavior: 'smooth', block: 'center' });
														}
													}, 150);
												}}
												className={`w-full cursor-pointer rounded border p-2 text-left text-xs transition-all ${
													isHighlighted
														? 'border-cyan-500 bg-cyan-500/10'
														: 'border-border bg-secondary/50 hover:border-border'
												}`}
											>
												<div className='flex items-start gap-2'>
													<span className='flex h-5 w-5 flex-shrink-0 items-center justify-center rounded bg-cyan-500/20 font-mono text-xs text-cyan-600 dark:text-cyan-400'>
														{citation.index}
													</span>
													<div className='min-w-0 flex-1'>
														<code className='block text-sky-600 dark:text-sky-400'>{citation.jsonPath}</code>
														<p className='text-muted-foreground mt-0.5 truncate'>{formatValue(value)}</p>
													</div>
												</div>
											</button>
										);
									})
								)}
							</div>
						) : (
							<div className='border-border/50 bg-card/80 max-h-[300px] overflow-auto rounded border p-2'>
								<div className='font-mono text-xs leading-relaxed'>
									<JsonViewer
										data={entry.rawData}
										basePath={stepPrefix}
										highlightedPaths={highlightedPaths}
										expandedPaths={expandedPaths}
										onPathClick={onCitationClick}
									/>
								</div>
							</div>
						))}
				</div>
			)}
		</div>
	);
}

export default function ProvenanceTab({
	dataPoolEntries = [],
	citations,
	highlightedPath,
	scrollRequest,
	onCitationClick
}: ProvenanceTabProps) {
	const [viewMode, setViewMode] = useState<'citations' | 'raw'>('citations');
	const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set([0])); // First step expanded by default

	// Filter citations to only those with valid (defined) values
	const validCitations = useMemo(() => {
		if (dataPoolEntries.length === 0) return citations;
		return citations.filter((c) => {
			const value = getValueAtPath(dataPoolEntries, c.jsonPath);
			return value !== undefined;
		});
	}, [citations, dataPoolEntries]);

	// Toggle step expansion
	const toggleStep = useCallback((stepIndex: number) => {
		setExpandedSteps((prev) => {
			const next = new Set(prev);
			if (next.has(stepIndex)) {
				next.delete(stepIndex);
			} else {
				next.add(stepIndex);
			}
			return next;
		});
	}, []);

	// Handle scroll request - expand the step and scroll to citation
	useEffect(() => {
		if (scrollRequest) {
			const { index } = scrollRequest;
			const citation = citations.find((c) => c.index === index);
			if (citation) {
				const { stepIndex } = parseStepPath(citation.jsonPath);
				setExpandedSteps((prev) => new Set(prev).add(stepIndex));
				setViewMode('citations');

				// Use setTimeout to wait for accordion expansion and DOM update
				setTimeout(() => {
					const element = document.getElementById(`citation-item-${index}`);
					if (element) {
						element.scrollIntoView({ behavior: 'smooth', block: 'center' });
					}
				}, 150); // Wait for React re-render and accordion animation
			}
		}
	}, [scrollRequest, citations]);

	const handleCitationClick = useCallback(
		(jsonPath: string) => {
			onCitationClick?.(jsonPath);
		},
		[onCitationClick]
	);

	if (dataPoolEntries.length === 0) {
		return (
			<div className='text-muted-foreground p-4 text-sm'>
				<p>No evidence yet.</p>
				<p className='mt-2 text-xs'>Evidence will appear here when the AI fetches and processes data.</p>
			</div>
		);
	}

	return (
		<div className='space-y-3'>
			{/* View Toggle - Single level of tabs */}
			<div className='flex items-center justify-between'>
				<div className='flex gap-2'>
					<button
						type='button'
						onClick={() => setViewMode('citations')}
						className={`rounded px-3 py-1.5 text-xs font-medium transition-colors ${
							viewMode === 'citations'
								? 'bg-cyan-500/20 text-cyan-600 dark:text-cyan-400'
								: 'bg-secondary text-muted-foreground hover:text-foreground'
						}`}
					>
						Citations ({validCitations.length})
					</button>
					<button
						type='button'
						onClick={() => setViewMode('raw')}
						className={`rounded px-3 py-1.5 text-xs font-medium transition-colors ${
							viewMode === 'raw'
								? 'bg-cyan-500/20 text-cyan-600 dark:text-cyan-400'
								: 'bg-secondary text-muted-foreground hover:text-foreground'
						}`}
					>
						Raw Data
					</button>
				</div>
			</div>

			{/* Step Accordions */}
			<div className='space-y-2'>
				{dataPoolEntries.map((entry, idx) => (
					<StepAccordion
						key={`${entry.stepId}-${idx}`}
						entry={entry}
						stepIndex={entry.stepIndex}
						isExpanded={expandedSteps.has(entry.stepIndex)}
						onToggle={() => toggleStep(entry.stepIndex)}
						citations={validCitations}
						highlightedPath={highlightedPath ?? null}
						onCitationClick={handleCitationClick}
						onSwitchToRaw={() => setViewMode('raw')}
						viewMode={viewMode}
						allEntries={dataPoolEntries}
					/>
				))}
			</div>
		</div>
	);
}
